import dagre from 'dagre'
import type { Edge, Node } from 'reactflow'
export interface StageConfigData {
  stages: Array<{
    id: string
    label: string
    color: string
    probability: number
    group: string
    order: number
    transitions: string[]
  }>
  transitions: Array<{
    from: string
    to: string
    color?: string
    label?: string
    dashed?: boolean
  }>
  stageOrder: string[]
  requiredFields: Record<string, { fields: string[]; labels: Record<string, string> }>
  groups: Array<{ id: string; label: string; stages: string[] }>
}

const SUBLABEL_MAP: Record<string, string> = {
  QUALIFICADO: 'Entrada pelo BDR / Prospecção',
  REUNIAO_AGENDADA: 'Aguardando reunião',
  REUNIAO_REALIZADA: 'Reunião concluída',
  PROPOSTA_ELABORACAO: 'Preparando documentação',
  PROPOSTA_ENVIADA: 'Aguardando retorno do cliente',
  RENEGOCIACAO: 'Ajuste de valores / Nova rodada',
  GERAR_CONTRATO: 'Documentação final',
  CONTRATO_ENVIADO: 'Aguardando assinatura',
  GANHO: 'Onboarding + Financeiro + Comissões',
  PERDIDO: 'Registro de motivo + submotivo',
  PAUSADO: 'Standby temporário',
  FECHAMENTO_FUTURO: 'Reativar negociação futura',
}

export function buildPipelineFlow(stageConfig: StageConfigData) {
  const { stages, transitions } = stageConfig

  // Dagre layout
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'LR', nodesep: 40, ranksep: 80, marginx: 30, marginy: 30 })

  stages.forEach(stage => {
    g.setNode(stage.id, { width: 180, height: 65 })
  })
  transitions.forEach(t => {
    g.setEdge(t.from, t.to)
  })

  dagre.layout(g)

  const nodes: Node[] = stages
    .map(stage => {
      const dagreNode = g.node(stage.id)
      if (!dagreNode) return null
      return {
        id: stage.id,
        type: 'custom' as const,
        position: { x: dagreNode.x - 90, y: dagreNode.y - 32 },
        data: {
          label: stage.label,
          sublabel: SUBLABEL_MAP[stage.id] || `${stage.probability}%`,
          color: stage.color,
          probability: stage.probability,
        },
      }
    })
    .filter(Boolean) as Node[]

  const edges: Edge[] = transitions
    .map(t => {
      if (t.from === t.to) return null
      const isOffRamp = t.to === 'PERDIDO' || t.to === 'PAUSADO' || t.to === 'FECHAMENTO_FUTURO'
      const edgeLabel = t.label ||
        (t.to === 'GANHO' ? '✓ Ganho' : undefined) ||
        (t.to === 'PERDIDO' ? '✗ Perda' : undefined) ||
        (t.to === 'PAUSADO' ? '⏸ Pausar' : undefined) ||
        (t.to === 'FECHAMENTO_FUTURO' ? '📅 Futuro' : undefined) ||
        (t.to === 'RENEGOCIACAO' ? '🔄 Renegociar' : undefined)

      const edge: Edge = {
        id: `e-${t.from}-${t.to}`,
        source: t.from,
        target: t.to,
        animated: !isOffRamp,
        type: 'smoothstep',
        style: {
          stroke: t.color || 'var(--brand-primary)',
          strokeWidth: isOffRamp ? 1.5 : 2,
          opacity: isOffRamp ? 0.45 : 1,
          ...(t.dashed ? { strokeDasharray: '5 5' } : {}),
        },
      }

      if (edgeLabel) {
        edge.label = edgeLabel
        edge.labelStyle = { fontSize: 9, fontWeight: 600, fill: t.color || '#fff', opacity: isOffRamp ? 0.6 : 1 }
        edge.labelBgStyle = { fill: '#141516', fillOpacity: 0.85 }
        edge.labelBgPadding = [4, 2] as [number, number]
        edge.labelBgBorderRadius = 4
      }

      if (t.to === 'GANHO') {
        edge.style = { ...edge.style, strokeWidth: 3, opacity: 1 }
      }

      return edge
    })
    .filter(Boolean) as Edge[]

  return { nodes, edges }
}
